/**
 * Shared date helpers for the Logs page's date-range controls
 * (LogsCalendarPicker, LogsDateRangeFilter, LogsFilterBar) — all three work
 * with the same two string formats: ISO `YYYY-MM-DD` (native
 * `<input type="date">` / calendar grid values) and the backend's
 * `MM-DD-YYYY`.
 */

// ── ISO <-> backend format conversion ────────────────────────────────────────

/** `YYYY-MM-DD` (native `<input type="date">` value) -> `MM-DD-YYYY` (backend's format). */
export function toBackendDate(isoDate: string): string {
  const [y, m, d] = isoDate.split('-');
  return `${m}-${d}-${y}`;
}

/** `MM-DD-YYYY` -> `YYYY-MM-DD`, to re-populate a native date input from an existing value. */
export function toInputDate(backendDate: string): string {
  const [m, d, y] = backendDate.split('-');
  return `${y}-${m}-${d}`;
}

/** `YYYY-MM-DD` -> `MM/DD/YYYY`, for the custom-range boxes' visible text (Figma node 2771-9908). */
export function formatDisplayDate(isoDate: string): string {
  const [y, m, d] = isoDate.split('-');
  return `${m}/${d}/${y}`;
}

// ── Events table + event details modal ───────────────────────────────────────

/**
 * "Jun 4, 2026 · 15:23 UTC" (Figma node 2774:11369) — always UTC/24-hour
 * regardless of the viewer's browser locale/timezone, since these are API
 * request timestamps and should read the same for every viewer.
 *
 * Shared by the events table (LogsPage) and the event details modal
 * (LogEventDetailsModal, sc-44238 State 2) so the Date column and the
 * modal's Date field can never drift apart.
 */
export function formatLogTimestamp(iso: string): string {
  const date = new Date(iso);
  const datePart = date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
  const timePart = date.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'UTC',
  });
  return `${datePart} · ${timePart} UTC`;
}

// ── Calendar grid helpers (LogsCalendarPicker) ───────────────────────────────

export function parseIsoDate(iso?: string): Date | null {
  if (!iso) return null;
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d);
}

export function toIso(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

export function shiftMonths(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth() + n, 1);
}

export function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

/** Sunday-first, 6-week (42-day) grid covering `viewMonth` plus its leading/trailing days. */
export function buildGrid(viewMonth: Date): Date[] {
  const first = startOfMonth(viewMonth);
  const gridStart = new Date(first.getFullYear(), first.getMonth(), 1 - first.getDay());
  return Array.from(
    { length: 42 },
    (_, i) => new Date(gridStart.getFullYear(), gridStart.getMonth(), gridStart.getDate() + i)
  );
}
